import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  FormGroup,
  Select,
  MenuItem,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { useLayout } from 'hooks/useLayout'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  LEUserRoleAndPermissionSchema,
  LeUserRoles,
} from './LEUserRoleAndPermissionSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSnackbar } from 'stores/useSnackbar'
import { PermissionControllerCheckbox } from './PermissionControllerCheckbox'
import { ActivityButton } from 'components/ActivityButton'
import { type useUserData } from 'hooks/api/useUserData'
import { useEffect, useState } from 'react'
import { type components } from 'api/playerPayback/api'
import { usePutLicensedEstablishmentUser } from 'hooks/api/usePutLicensedEstablishmentUser'
import {
  type LicensedEstablishmentUserLicensedEstablishment,
  type UpdateLicensedEstablishmentUserRequest,
} from 'src/types/api'
import {
  isPermissionOwner,
  isPermissionStaff,
  type permissionFormDataType,
} from 'utils/permissions'
import { AuthorizeSweepstakesModal } from './AuthorizeSweepstakesModal'
import { colorPrimitives } from 'components/Theme'

enum AccountTypes {
  CorporateAccount,
  Organization,
  LicensedEstablishment,
}

interface EditLeUserRoleAndPermissionsModalProps {
  isModalOpen: boolean
  onClose: () => void
  accountId?: number
  accountType?: AccountTypes
  accountName: string | undefined
  user: ReturnType<typeof useUserData>['user']
  licensedEstablishments: Array<
    components['schemas']['FullLicensedEstablishmentDTO']
  >
}

const generateDefaultValues = (
  leData?: LicensedEstablishmentUserLicensedEstablishment
) => {
  const lePermissions = leData
  return {
    type: lePermissions?.type,
    allPermissions: false,
    staffPermissions: false,
    flexPayPermission: lePermissions?.flexPayPermission ?? false,
    compliancePermission: lePermissions?.compliancePermission ?? false,
    gamingReportsPermission: lePermissions?.gamingReportsPermission ?? false,
    playerPaybackPermission: lePermissions?.playerPaybackPermission ?? false,
    patronCheckInsPermission: lePermissions?.patronCheckInsPermission ?? false,
    rewardsPermission: lePermissions?.rewardsPermission ?? false,
    promotionsPermission: lePermissions?.promotionsPermission ?? false,
    sweepstakesPermission: lePermissions?.sweepstakesPermission ?? false,
    enrollInSweepstakesPermission:
      lePermissions?.enrollInSweepstakesPermission ?? false,
    locationSweepstakesPermission:
      lePermissions?.locationSweepstakesPermission ?? false,
    playerPaybackReportsPermission:
      lePermissions?.playerPaybackReportsPermission ?? false,
    locationsPermission: lePermissions?.locationsPermission ?? false,
    leUsersPermission: lePermissions?.leUsersPermission ?? false,
    contactUsPermission: lePermissions?.contactUsPermission ?? false,
    reportsAccessToPIIPermission:
      lePermissions?.reportsAccessToPIIPermission ?? false,
    reportsDownloadPIIPermission:
      lePermissions?.reportsDownloadPIIPermission ?? false,
    badgeRewardsPermission: lePermissions?.badgeRewardsPermission ?? false,
    badgeLocationsPermission: lePermissions?.badgeLocationsPermission ?? false,
  }
}

export const EditLeUserRoleAndPermissionsModal = ({
  isModalOpen,
  onClose,
  accountId,
  accountType,
  accountName,
  licensedEstablishments,
  user,
}: EditLeUserRoleAndPermissionsModalProps) => {
  const { setMessage } = useSnackbar()
  const { isMobile } = useLayout()

  let leUserLEData: any
  const [corporateAccountId, setCorporateAccountId] = useState<number | null>()
  const [organizationId, setOrganizationId] = useState<number | null>()
  const [licensedEstablishmentId, setLicensedEstablishmentId] = useState<
    number | null
  >()
  const [isOwner, setIsOwner] = useState<boolean>(false)
  const [isStaff, setIsStaff] = useState<boolean>(false)
  const [isSweepstakeModelOpen, setIsSweepstakeModelOpen] = useState(false)
  const toggleSweepstakeModalOpen = () =>
    setIsSweepstakeModelOpen((prev) => !prev)

  const formMethods = useForm({
    resolver: yupResolver(LEUserRoleAndPermissionSchema),
    mode: 'onSubmit',
    defaultValues: generateDefaultValues(
      user?.licensedEstablishments?.filter(
        (x) =>
          x.licensedEstablishmentUserId === user?.id &&
          x.licensedEstablishmentId === accountId
      )[0] ?? undefined
    ),
  })

  const { handleSubmit, setValue, getValues, reset, watch } = formMethods

  const myType = watch('type')

  useEffect(() => {
    switch (accountType) {
      case AccountTypes.CorporateAccount:
        setCorporateAccountId(accountId)
        setOrganizationId(null)
        setLicensedEstablishmentId(null)

        reset(generateDefaultValues())
        break
      case AccountTypes.Organization:
        setCorporateAccountId(null)
        setOrganizationId(accountId)
        setLicensedEstablishmentId(null)

        reset(generateDefaultValues())
        break
      case AccountTypes.LicensedEstablishment:
        setCorporateAccountId(null)
        setOrganizationId(null)
        setLicensedEstablishmentId(accountId)

        reset(
          generateDefaultValues(
            user?.licensedEstablishments?.filter(
              (x) =>
                x.licensedEstablishmentUserId === user?.id &&
                x.licensedEstablishmentId === accountId
            )[0] ?? undefined
          )
        )
        break
      default:
        break
    }
    formIsEverythingSelected()
    formIsStaffSelected()
  }, [accountId, user])

  const mutation = usePutLicensedEstablishmentUser({
    licensedEstablishmentUserId: Number(user?.id),
    corporateAccountId,
    organizationId,
    licensedEstablishmentId,
    onError: async () => {
      setMessage('An Error Occurred Updating Roles and Permissions', 'error')
      onClose()
    },
    onSuccess: async () => {
      setMessage('Successfully updated Roles and Permissions', 'success')
      onClose()

      // Refresh the permissions page to reset menu items if needed and refresh data displayed on pages
      // Otherwise, if you navigate to another page you may see data that you don't have access to
      window.location.reload()
    },
  })

  const formIsEverythingSelected = (): boolean => {
    const values = getValues()
    delete values.staffPermissions
    delete values.allPermissions

    const isOwner = isPermissionOwner(values)

    setIsOwner(isOwner)

    setValue('allPermissions', isOwner)

    return isOwner
  }

  const formIsStaffSelected = (): boolean => {
    const values = getValues()
    delete values.staffPermissions

    const isStaff = isPermissionStaff(values)

    setIsStaff(isStaff)

    return isStaff
  }

  const handleChange = (key: permissionFormDataType) => {
    const currentValue = getValues(key)
    setValue(key, !currentValue)
    setValue('allPermissions', formIsEverythingSelected())
    setValue('staffPermissions', formIsStaffSelected())
  }

  const onSweepstakeModalAccept = () => {
    handleChange('enrollInSweepstakesPermission')
    setValue('allPermissions', formIsEverythingSelected())
  }

  return (
    <>
      <Dialog
        fullScreen={isMobile}
        open={isModalOpen}
        onClose={() => {
          onClose()
        }}
        maxWidth={'xl'}
        fullWidth
        PaperProps={{ sx: { p: 0, maxWidth: '800px' } }}
      >
        <DialogTitle bgcolor={grey[50]} sx={{ p: 0 }}>
          <Box px={4} py={2}>
            <Typography variant="h3">
              Edit Roles and Permissions - {accountName}
            </Typography>
            {accountType !== AccountTypes.LicensedEstablishment ? (
              <Typography variant="body-1" color={'text.secondary'}>
                {`Updating access on this screen will apply the access to all Organizations or Licensed Establishments linked to this Account, so access levels aren't stored here and will clear after saving.`}
              </Typography>
            ) : (
              ''
            )}
          </Box>
        </DialogTitle>
        <form
          onSubmit={handleSubmit((data) => {
            const caId =
              licensedEstablishments?.filter(
                (x) => x.corporateAccountId === accountId
              )[0] ?? 0
            const orgId =
              licensedEstablishments?.filter(
                (x) => x.organizationId === accountId
              )[0] ?? 0

            switch (accountType) {
              case AccountTypes.CorporateAccount:
                setCorporateAccountId(accountId)
                setOrganizationId(null)
                setLicensedEstablishmentId(null)

                leUserLEData =
                  user?.licensedEstablishments?.filter(
                    (x) =>
                      x.licensedEstablishmentUserId === user?.id &&
                      x.licensedEstablishmentId === caId.id
                  )[0] ?? undefined
                break
              case AccountTypes.Organization:
                setCorporateAccountId(null)
                setOrganizationId(accountId)
                setLicensedEstablishmentId(null)

                leUserLEData =
                  user?.licensedEstablishments?.filter(
                    (x) =>
                      x.licensedEstablishmentUserId === user?.id &&
                      x.licensedEstablishmentId === orgId.id
                  )[0] ?? undefined

                break
              case AccountTypes.LicensedEstablishment:
                setCorporateAccountId(null)
                setOrganizationId(null)
                setLicensedEstablishmentId(accountId)

                leUserLEData =
                  user?.licensedEstablishments?.filter(
                    (x) =>
                      x.licensedEstablishmentUserId === user?.id &&
                      x.licensedEstablishmentId === accountId
                  )[0] ?? undefined
                break
              default:
                break
            }

            const updateData = data
            delete updateData.allPermissions
            delete updateData.staffPermissions
            const mutationRequest: UpdateLicensedEstablishmentUserRequest = {
              ...updateData,
              email: String(user?.email),
              firstName: user?.firstName,
              lastName: user?.lastName,
              type: updateData.type ?? 'Staff',
            }
            mutation.mutate(mutationRequest)
          })}
        >
          <FormProvider {...formMethods}>
            <DialogContent
              sx={{ p: 0, zIndex: 0, overflow: 'auto', height: '70vh' }}
            >
              <Grid px={{ xs: 2, sm: 4 }} pb={{ xs: 3, sm: 4 }} container>
                <Grid item md={6} xs={12} pt={{ md: '10px', xs: '8px' }}>
                  <Typography>Roles</Typography>
                </Grid>
                <Grid item md={6} xs={12} pt={{ md: '10px', xs: '8px' }}>
                  <Controller
                    name={'type'}
                    control={formMethods.control}
                    render={({ field }) => (
                      <Select
                        aria-label="type"
                        {...field}
                        labelId={'roles'}
                        name={'Roles'}
                        variant="outlined"
                        required={true}
                        fullWidth
                        defaultValue={leUserLEData?.type ?? ''}
                        displayEmpty
                        renderValue={(selectedItem) => {
                          return selectedItem ? (
                            <Typography>{selectedItem}</Typography>
                          ) : (
                            <em>Select Role</em>
                          )
                        }}
                        onChange={(e) => {
                          const selectedRole = e.target.value as
                            | 'Owner'
                            | 'Staff'
                          setValue('type', selectedRole)

                          if (selectedRole === 'Owner') {
                            setValue('allPermissions', true)
                            setValue('staffPermissions', false)
                            setValue('flexPayPermission', true)
                            setValue('compliancePermission', true)
                            setValue('gamingReportsPermission', true)
                            setValue('playerPaybackPermission', true)
                            setValue('patronCheckInsPermission', true)
                            setValue('rewardsPermission', true)
                            setValue('promotionsPermission', true)
                            setValue('sweepstakesPermission', true)
                            setValue('enrollInSweepstakesPermission', true)
                            setValue('locationSweepstakesPermission', true)
                            setValue('playerPaybackReportsPermission', true)
                            setValue('locationsPermission', true)
                            setValue('leUsersPermission', true)
                            setValue('contactUsPermission', true)
                            setValue('badgeLocationsPermission', false)
                            setIsOwner(true)
                            setIsStaff(false)
                          } else {
                            setValue('allPermissions', false)
                            setValue('staffPermissions', true)
                            setValue('flexPayPermission', false)
                            setValue('compliancePermission', true)
                            setValue('gamingReportsPermission', false)
                            setValue('playerPaybackPermission', true)
                            setValue('patronCheckInsPermission', false)
                            setValue('rewardsPermission', false)
                            setValue('promotionsPermission', true)
                            setValue('sweepstakesPermission', false)
                            setValue('enrollInSweepstakesPermission', false)
                            setValue('locationSweepstakesPermission', true)
                            setValue('playerPaybackReportsPermission', true)
                            setValue('locationsPermission', false)
                            setValue('leUsersPermission', false)
                            setValue('contactUsPermission', false)
                            setValue('badgeRewardsPermission', false)
                            setValue('badgeLocationsPermission', false)
                            setIsOwner(false)
                            setIsStaff(true)
                          }
                        }}
                      >
                        {LeUserRoles?.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type ?? ''}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Grid>
                <Grid item md={6} xs={12} pt={{ md: '10px', xs: '8px' }}>
                  <Typography>Permissions</Typography>
                </Grid>
                <Grid item md={6} xs={12} pt={{ md: '10px', xs: '8px' }}>
                  <FormGroup>
                    {myType !== 'LimitedLiveAtLocation' && (
                      <PermissionControllerCheckbox
                        label="Select All"
                        name="allPermissions"
                        control={formMethods.control}
                        onChange={() => {
                          const newValue = !getValues('allPermissions')
                          setValue('staffPermissions', false)
                          setValue('allPermissions', newValue)
                          setValue('flexPayPermission', newValue)
                          setValue('compliancePermission', newValue)
                          setValue('gamingReportsPermission', newValue)
                          setValue('playerPaybackPermission', newValue)
                          setValue('patronCheckInsPermission', newValue)
                          setValue('rewardsPermission', newValue)
                          setValue('promotionsPermission', newValue)
                          setValue('sweepstakesPermission', newValue)
                          setValue('locationSweepstakesPermission', newValue)
                          setValue('playerPaybackReportsPermission', newValue)
                          setValue('locationsPermission', newValue)
                          setValue('leUsersPermission', newValue)
                          setValue('contactUsPermission', newValue)
                          setValue('badgeRewardsPermission', newValue)
                          setValue('badgeLocationsPermission', newValue)
                          // If Role is 'Owner', reverse flag because the below code that calls 'handleChange('enrollInSweepstakesPermission')' will set the checkbox correctly when Select All is clicked.
                          setValue('enrollInSweepstakesPermission', !newValue)
                          handleChange('allPermissions')
                          const currentPermissionChecked = getValues(
                            'enrollInSweepstakesPermission'
                          )
                          const currentRole = getValues('type')
                          if (
                            currentRole === 'Staff' &&
                            !currentPermissionChecked
                          ) {
                            setIsSweepstakeModelOpen(true)
                          } else {
                            handleChange('enrollInSweepstakesPermission')
                          }
                        }}
                      />
                    )}

                    <PermissionControllerCheckbox
                      label="FlexPay"
                      name="flexPayPermission"
                      control={formMethods.control}
                      onChange={() => {
                        handleChange('flexPayPermission')
                      }}
                    />

                    <PermissionControllerCheckbox
                      label="Compliance"
                      name="compliancePermission"
                      control={formMethods.control}
                      onChange={() => {
                        handleChange('compliancePermission')
                      }}
                    />

                    <PermissionControllerCheckbox
                      label="Gaming Reports"
                      name="gamingReportsPermission"
                      control={formMethods.control}
                      onChange={() => {
                        handleChange('gamingReportsPermission')
                      }}
                    />

                    <PermissionControllerCheckbox
                      label="Player PAYBACK"
                      name="playerPaybackPermission"
                      control={formMethods.control}
                      onChange={() => {
                        const currentPlayerPaybackPermission = getValues(
                          'playerPaybackPermission'
                        )
                        const currentPermissionChecked = getValues(
                          'enrollInSweepstakesPermission'
                        )
                        const currentRole = getValues('type')
                        if (
                          currentRole === 'Staff' &&
                          !currentPlayerPaybackPermission &&
                          !currentPermissionChecked
                        ) {
                          handleChange('playerPaybackPermission')
                          const newValue = getValues('playerPaybackPermission')
                          setValue('patronCheckInsPermission', newValue)
                          setValue('rewardsPermission', newValue)
                          setValue('promotionsPermission', newValue)
                          setValue('sweepstakesPermission', newValue)
                          setValue('locationSweepstakesPermission', newValue)
                          setValue('playerPaybackReportsPermission', newValue)
                          setValue('badgeRewardsPermission', newValue)
                          setValue('badgeLocationsPermission', newValue)
                          setIsSweepstakeModelOpen(true)
                        } else {
                          handleChange('playerPaybackPermission')
                          const newValue = getValues('playerPaybackPermission')
                          setValue('patronCheckInsPermission', newValue)
                          setValue('rewardsPermission', newValue)
                          setValue('promotionsPermission', newValue)
                          setValue('sweepstakesPermission', newValue)
                          setValue('enrollInSweepstakesPermission', newValue)
                          setValue('locationSweepstakesPermission', newValue)
                          setValue('playerPaybackReportsPermission', newValue)
                          setValue('badgeRewardsPermission', newValue)
                          setValue('badgeLocationsPermission', newValue)
                          setValue('allPermissions', formIsEverythingSelected())
                        }
                      }}
                    />

                    <FormGroup sx={{ pl: '32px' }}>
                      <PermissionControllerCheckbox
                        label="Patron Check Ins"
                        name="patronCheckInsPermission"
                        control={formMethods.control}
                        onChange={() => {
                          handleChange('patronCheckInsPermission')
                        }}
                      />

                      <PermissionControllerCheckbox
                        label="Rewards"
                        name="rewardsPermission"
                        control={formMethods.control}
                        onChange={() => {
                          handleChange('rewardsPermission')
                        }}
                      />

                      <PermissionControllerCheckbox
                        label="Specials & Events"
                        name="promotionsPermission"
                        control={formMethods.control}
                        onChange={() => {
                          handleChange('promotionsPermission')
                        }}
                      />

                      <PermissionControllerCheckbox
                        label="J&J Sweepstakes"
                        name="sweepstakesPermission"
                        control={formMethods.control}
                        onChange={() => {
                          const currentSweepstakesPermission = getValues(
                            'sweepstakesPermission'
                          )
                          const currentPermissionChecked = getValues(
                            'enrollInSweepstakesPermission'
                          )
                          const currentRole = getValues('type')
                          if (
                            currentRole === 'Staff' &&
                            !currentSweepstakesPermission &&
                            !currentPermissionChecked
                          ) {
                            handleChange('sweepstakesPermission')
                            setIsSweepstakeModelOpen(true)
                          } else {
                            handleChange('sweepstakesPermission')
                            const newValue = getValues('sweepstakesPermission')
                            setValue('enrollInSweepstakesPermission', newValue)
                            setValue(
                              'allPermissions',
                              formIsEverythingSelected()
                            )
                          }
                        }}
                      />

                      <FormGroup sx={{ pl: '32px' }}>
                        <PermissionControllerCheckbox
                          label="Enroll In Sweepstakes"
                          name="enrollInSweepstakesPermission"
                          control={formMethods.control}
                          onChange={() => {
                            const currentPermissionChecked = getValues(
                              'enrollInSweepstakesPermission'
                            )
                            const currentRole = getValues('type')
                            if (
                              currentRole === 'Staff' &&
                              !currentPermissionChecked
                            ) {
                              setIsSweepstakeModelOpen(true)
                            } else {
                              handleChange('enrollInSweepstakesPermission')
                            }
                          }}
                        />
                      </FormGroup>

                      <PermissionControllerCheckbox
                        label="Location Sweepstakes"
                        name="locationSweepstakesPermission"
                        control={formMethods.control}
                        onChange={() => {
                          handleChange('locationSweepstakesPermission')
                        }}
                      />

                      <PermissionControllerCheckbox
                        label="Player PAYBACK® Reports"
                        name="playerPaybackReportsPermission"
                        control={formMethods.control}
                        onChange={() => {
                          handleChange('playerPaybackReportsPermission')
                        }}
                      />

                      {/* <PermissionControllerCheckbox
                        label="Badge Rewards"
                        name="badgeRewardsPermission"
                        control={formMethods.control}
                        onChange={() => {
                          handleChange('badgeRewardsPermission')
                        }}
                      /> */}

                      {/* <PermissionControllerCheckbox
                        label="Location Badges"
                        name="badgeLocationsPermission"
                        control={formMethods.control}
                        onChange={() => {
                          handleChange('badgeLocationsPermission')
                        }}
                      /> */}
                    </FormGroup>

                    <PermissionControllerCheckbox
                      label="Locations"
                      name="locationsPermission"
                      control={formMethods.control}
                      onChange={() => {
                        handleChange('locationsPermission')
                      }}
                    />

                    <PermissionControllerCheckbox
                      label="J&J Connect Users"
                      name="leUsersPermission"
                      control={formMethods.control}
                      onChange={() => {
                        handleChange('leUsersPermission')
                      }}
                    />

                    <PermissionControllerCheckbox
                      label="Contact Us"
                      name="contactUsPermission"
                      control={formMethods.control}
                      onChange={() => {
                        handleChange('contactUsPermission')
                      }}
                    />
                  </FormGroup>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className="border-t">
              <Grid
                item
                md={6}
                xs={12}
                pr={{ md: '150px' }}
                pt={{ md: '10px', xs: '8px' }}
              >
                {myType && !isOwner && !isStaff && (
                  <Typography
                    sx={{
                      color: colorPrimitives.lightCyanBlue,
                    }}
                  >
                    You’ve Chosen Custom Permissions!
                  </Typography>
                )}
              </Grid>
              <Box className="flex flex-row justify-end py-4 md:px-8 sm:px-4">
                <Button
                  onClick={() => {
                    onClose()
                  }}
                >
                  Cancel
                </Button>
                <ActivityButton
                  active={mutation.isPending}
                  variant="contained"
                  type="submit"
                >
                  Save
                </ActivityButton>
              </Box>
            </DialogActions>
          </FormProvider>
        </form>
      </Dialog>
      <AuthorizeSweepstakesModal
        isModalOpen={isSweepstakeModelOpen}
        onClose={toggleSweepstakeModalOpen}
        onSweepstakeModalAccept={onSweepstakeModalAccept}
      />
    </>
  )
}
